import type {
  MarketingCampaign,
  MarketingCampaignBudget,
  MarketingCampaignBudgetScheduled,
  MarketingCampaignBudgetScheduleWindow,
  MarketingCampaignBudgetSegment,
  MarketingCampaignBudgetSimple,
} from "../interfaces/KYDMarketing";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { scheduleInputToUtcIso } from "./adsetSchedule";
import { minorAmountToPrice } from "./minorAmountToPrice";

dayjs.extend(utc);
dayjs.extend(timezone);

/** Meta platform upper bound for phased schedule rows (reference; KYD caps lower). */
export const META_BUDGET_SCHEDULE_MAX_SEGMENTS = 50;

/** KYD product cap on scheduled budget phases (segments); API + UI + timeline validation. */
export const SCHEDULED_BUDGET_MAX_SEGMENTS = 10;

/** Minimum phased rows for scheduled budgets (create, edit, launch, publish). */
export const SCHEDULED_BUDGET_MIN_SEGMENTS = 2;

export const SCHEDULED_BUDGET_MIN_SEGMENTS_MESSAGE =
  "Add at least two budget phases";

/**
 * Meta rejects `budget_schedules` times that are not on a 15-minute grid (UTC):
 * minute must be 0, 15, 30, or 45 (error_subcode 3858205).
 */
export const META_BUDGET_SCHEDULE_INTERVAL_SEC = 15 * 60;

/** Meta rejects overlapping high-demand `budget_schedules` windows (`error_subcode` 3858167). */
export const META_BUDGET_SCHEDULE_OVERLAP_ERROR_SUBCODE = 3858167;

/**
 * Product rule (KYD): each phased campaign budget segment must span at least this wall-clock
 * duration between `time_start` and `time_end` (ISO instants). Enforced in
 * {@link validateBudgetSegments}, UI timeline checks, and create/edit APIs.
 * Max segment count uses {@link SCHEDULED_BUDGET_MAX_SEGMENTS}.
 */
export const SCHEDULED_BUDGET_PHASE_MIN_DURATION_HOURS = 3;
export const SCHEDULED_BUDGET_PHASE_MIN_DURATION_SECONDS =
  SCHEDULED_BUDGET_PHASE_MIN_DURATION_HOURS * 60 * 60;

export const SCHEDULED_BUDGET_PHASE_MIN_DURATION_MESSAGE = `Each budget phase must span at least ${SCHEDULED_BUDGET_PHASE_MIN_DURATION_HOURS} hours.`;

/**
 * Snap a Unix timestamp (seconds, UTC) to Meta's required 15-minute boundary.
 */
export function snapUnixSecondsToMetaBudgetScheduleUtc(
  unixSeconds: number,
  mode: "floor" | "ceil"
): number {
  const q = META_BUDGET_SCHEDULE_INTERVAL_SEC;
  return mode === "floor"
    ? Math.floor(unixSeconds / q) * q
    : Math.ceil(unixSeconds / q) * q;
}

/**
 * Align **campaign run** flight instants (UTC ISO) to Meta’s 15-minute UTC grid before comparing
 * phased budget segments, which are authored on that same grid (see guided
 * `snapWallMinuteToMetaBudgetUtc` / {@link initialTwoScheduledPhaseWallRows}).
 *
 * Without this, a run start like `13:07Z` fails validation against a first phase floored to
 * `13:00Z` (“phases must not start before the ad run schedule”) even though the UI is consistent.
 */
export function snapUtcFlightIsoBoundsToMetaBudgetScheduleGrid(params: {
  flightStartIso: string;
  flightEndIso: string;
}): { flightStartIso: string; flightEndIso: string } {
  const startMs = Date.parse(params.flightStartIso.trim());
  const endMs = Date.parse(params.flightEndIso.trim());
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    return {
      flightStartIso: params.flightStartIso.trim(),
      flightEndIso: params.flightEndIso.trim(),
    };
  }
  const q = META_BUDGET_SCHEDULE_INTERVAL_SEC;
  const startSec = snapUnixSecondsToMetaBudgetScheduleUtc(
    Math.floor(startMs / 1000),
    "floor"
  );
  let endSec = snapUnixSecondsToMetaBudgetScheduleUtc(
    Math.floor(endMs / 1000),
    "ceil"
  );
  if (endSec <= startSec) {
    endSec = startSec + q;
  }
  return {
    flightStartIso: new Date(startSec * 1000).toISOString(),
    flightEndIso: new Date(endSec * 1000).toISOString(),
  };
}

/**
 * Snap a single UTC ISO instant to Meta’s 15m grid — **floor** for phase starts (matches guided
 * `snapWallMinuteToMetaBudgetUtc` `start`) and **ceil** for phase ends (`end`).
 */
export function snapUtcIsoInstantToMetaBudgetScheduleGrid(
  iso: string,
  bound: "start" | "end"
): string {
  const trimmed = iso.trim();
  const ms = Date.parse(trimmed);
  if (!Number.isFinite(ms)) {
    return trimmed;
  }
  const sec = snapUnixSecondsToMetaBudgetScheduleUtc(
    Math.floor(ms / 1000),
    bound === "start" ? "floor" : "ceil"
  );
  return new Date(sec * 1000).toISOString();
}

function resolveScheduledBudgetEventIana(eventTimezone: string | undefined): string {
  const t = (eventTimezone ?? "").trim();
  return t.length > 0 ? t : "UTC";
}

/**
 * Event-wall `YYYY-MM-DDTHH:mm` in `eventIana` → Meta 15m UTC snap (**floor**, same as guided
 * `snapWallMinuteToMetaBudgetUtc` with `start`), returned as UTC ISO.
 */
export function snapEventWallMinuteToMetaBudgetUtcIso(
  wallMinute: string,
  eventIana: string
): string {
  const wall = wallMinute.trim().slice(0, 16);
  const tz = resolveScheduledBudgetEventIana(eventIana);
  const d = dayjs.tz(wall, tz);
  if (!d.isValid()) {
    return wallMinute.trim();
  }
  const unixSeconds = Math.floor(d.valueOf() / 1000);
  const snapped = snapUnixSecondsToMetaBudgetScheduleUtc(unixSeconds, "floor");
  return new Date(snapped * 1000).toISOString();
}

/**
 * Event-wall `YYYY-MM-DDTHH:mm` at **start of the inclusive last calendar day** for a phase
 * whose end instant is `phaseEndUtcIso` (UTC) in `eventIana`.
 */
function inclusiveDayStartWallMinuteFromPhaseEndUtcIso(
  phaseEndUtcIso: string,
  eventIana: string
): string | null {
  const tz = resolveScheduledBudgetEventIana(eventIana);
  const z = dayjs.utc(phaseEndUtcIso.trim()).tz(tz);
  if (!z.isValid()) return null;
  const atLocalMidnight =
    z.millisecond() === 0 &&
    z.second() === 0 &&
    z.minute() === 0 &&
    z.hour() === 0;
  const inclusiveDayStart = atLocalMidnight
    ? z.subtract(1, "day").startOf("day")
    : z.startOf("day");
  return inclusiveDayStart.format("YYYY-MM-DDTHH:mm");
}

/**
 * From an inclusive last-day **start-of-day** wall minute, the following calendar midnight in the
 * same zone (`YYYY-MM-DDTHH:mm`, not yet Meta-snapped on the UTC grid).
 */
export function exclusiveNextLocalMidnightWallFromInclusiveDayStartWallMinute(
  inclusiveDayStartWallMinute: string,
  eventIana: string
): string {
  const tz = resolveScheduledBudgetEventIana(eventIana);
  const wall = inclusiveDayStartWallMinute.trim().slice(0, 16);
  const d = dayjs.tz(wall, tz);
  if (!d.isValid()) return "";
  return d.add(1, "day").startOf("day").format("YYYY-MM-DDTHH:mm");
}

/**
 * Stored phase `time_end` (UTC ISO): exclusive next local midnight after the inclusive last
 * calendar day, Meta-snapped. Composes {@link exclusiveNextLocalMidnightWallFromInclusiveDayStartWallMinute}
 * and {@link snapEventWallMinuteToMetaBudgetUtcIso}.
 */
export function scheduledBudgetExclusivePhaseEndUtcIsoFromInclusiveDayStartWallMinute(
  inclusiveDayStartWallMinute: string,
  eventIana: string
): string {
  const nextWall = exclusiveNextLocalMidnightWallFromInclusiveDayStartWallMinute(
    inclusiveDayStartWallMinute,
    eventIana
  );
  if (!nextWall) return "";
  return snapEventWallMinuteToMetaBudgetUtcIso(nextWall, eventIana);
}

/** Calendar-day junction between phased segments: UTC phase end → exclusive next midnight (UTC ISO). */
export function scheduledBudgetPhaseEndJunctionUtcIsoFromSegmentEnd(
  phaseEndUtcIso: string,
  eventIana: string
): string {
  const trimmed = phaseEndUtcIso.trim();
  const inclusiveWall = inclusiveDayStartWallMinuteFromPhaseEndUtcIso(
    trimmed,
    eventIana
  );
  if (inclusiveWall == null) return trimmed;
  const junction =
    scheduledBudgetExclusivePhaseEndUtcIsoFromInclusiveDayStartWallMinute(
      inclusiveWall,
      eventIana
    );
  return junction || trimmed;
}

function proposedCalendarMidnightJunctionsUtc(
  segments: MarketingCampaignBudgetSegment[],
  eventIana: string
): string[] {
  const junctions: string[] = [];
  for (let i = 0; i < segments.length - 1; i++) {
    junctions.push(
      scheduledBudgetPhaseEndJunctionUtcIsoFromSegmentEnd(
        segments[i].time_end,
        eventIana
      )
    );
  }
  return junctions;
}

function clampJunctionsToPerIndexFlightFeasibleBands(
  junctions: string[],
  flightStartUnix: number,
  flightEndUnix: number,
  minPhaseSeconds: number
): void {
  const m = junctions.length;
  for (let i = 0; i < m; i++) {
    const raw = parseIsoToUnixSeconds(junctions[i]!);
    if (raw == null) continue;
    const earliest = flightStartUnix + minPhaseSeconds * (i + 1);
    const latest = flightEndUnix - minPhaseSeconds * (m - i);
    if (latest <= earliest) continue;
    let clamped = Math.min(Math.max(raw, earliest), latest);
    clamped = snapUnixSecondsToMetaBudgetScheduleUtc(clamped, "floor");
    clamped = Math.min(Math.max(clamped, earliest), latest);
    junctions[i] = new Date(clamped * 1000).toISOString();
  }
}

function pullLaterJunctionsUpToMinGapAfterPrior(
  junctions: string[],
  flightEndUnix: number,
  minPhaseSeconds: number
): void {
  const m = junctions.length;
  for (let i = 1; i < m; i++) {
    const prev = parseIsoToUnixSeconds(junctions[i - 1]!);
    let cur = parseIsoToUnixSeconds(junctions[i]!);
    if (prev == null || cur == null) continue;
    if (cur >= prev + minPhaseSeconds) continue;
    const latest = flightEndUnix - minPhaseSeconds * (m - i);
    let bumped = Math.min(prev + minPhaseSeconds, latest);
    bumped = snapUnixSecondsToMetaBudgetScheduleUtc(bumped, "floor");
    bumped = Math.max(bumped, prev + minPhaseSeconds);
    bumped = Math.min(bumped, latest);
    junctions[i] = new Date(bumped * 1000).toISOString();
  }
}

function pullEarlierJunctionsDownToMinGapBeforeNext(
  junctions: string[],
  flightStartUnix: number,
  minPhaseSeconds: number
): void {
  const m = junctions.length;
  for (let i = m - 2; i >= 0; i--) {
    const next = parseIsoToUnixSeconds(junctions[i + 1]!);
    let cur = parseIsoToUnixSeconds(junctions[i]!);
    if (next == null || cur == null) continue;
    if (cur <= next - minPhaseSeconds) continue;
    const earliest = flightStartUnix + minPhaseSeconds * (i + 1);
    let bumped = Math.max(next - minPhaseSeconds, earliest);
    bumped = snapUnixSecondsToMetaBudgetScheduleUtc(bumped, "floor");
    bumped = Math.min(bumped, next - minPhaseSeconds);
    bumped = Math.max(bumped, earliest);
    junctions[i] = new Date(bumped * 1000).toISOString();
  }
}

/**
 * Rewrites scheduled budget segment instants so internal boundaries sit on **event-local calendar
 * midnights** (Meta 15m UTC floor on those walls), and the first/last instants match the snapped
 * ad flight window. Call after wall→UTC conversion and before {@link validateScheduledCampaignBudget}
 * / {@link validateScheduledBudgetWithinAdFlight}.
 */
export function normalizeScheduledBudgetSegmentsToEventDayJunctions(params: {
  segments: MarketingCampaignBudgetSegment[];
  eventIana: string;
  flightStartIso: string;
  flightEndIso: string;
}): MarketingCampaignBudgetSegment[] {
  const { segments, eventIana } = params;
  const n = segments.length;
  if (n === 0) return segments;

  const flight = snapUtcFlightIsoBoundsToMetaBudgetScheduleGrid({
    flightStartIso: params.flightStartIso.trim(),
    flightEndIso: params.flightEndIso.trim(),
  });
  const fs = snapUtcIsoInstantToMetaBudgetScheduleGrid(
    flight.flightStartIso,
    "start"
  );
  const fe = snapUtcIsoInstantToMetaBudgetScheduleGrid(flight.flightEndIso, "end");

  const junctions = proposedCalendarMidnightJunctionsUtc(segments, eventIana);

  const fsSec = parseIsoToUnixSeconds(fs);
  const feSec = parseIsoToUnixSeconds(fe);
  const minSeg = SCHEDULED_BUDGET_PHASE_MIN_DURATION_SECONDS;
  if (fsSec != null && feSec != null && junctions.length > 0) {
    clampJunctionsToPerIndexFlightFeasibleBands(junctions, fsSec, feSec, minSeg);
    pullLaterJunctionsUpToMinGapAfterPrior(junctions, feSec, minSeg);
    pullEarlierJunctionsDownToMinGapBeforeNext(junctions, fsSec, minSeg);
  }

  return segments.map((s, i) => ({
    ...s,
    time_start: i === 0 ? fs : junctions[i - 1]!,
    time_end: i === n - 1 ? fe : junctions[i]!,
  }));
}

/**
 * When the campaign uses a scheduled budget and ad sets define a combined flight window,
 * normalize segment boundaries (see {@link normalizeScheduledBudgetSegmentsToEventDayJunctions}).
 * The first segment’s `time_start` and last segment’s `time_end` are pinned to that **combined
 * campaign run** (snapped min ad-set `start_time` / max `stop_time`). Pass a `campaign` whose
 * `adsets` already reflect any ad-set schedule patches from the same update so flight matches
 * persisted run bounds.
 * Otherwise returns `campaign` unchanged.
 */
export function normalizeMarketingCampaignScheduledBudgetDayJunctions(
  campaign: MarketingCampaign,
  eventIana: string
): MarketingCampaign {
  if (campaign.budget?.type !== "scheduled") {
    return campaign;
  }
  const bounds = deriveCampaignFlightIsoBoundsFromAdsets(campaign.adsets ?? []);
  if (!bounds) {
    return campaign;
  }
  const tz = resolveScheduledBudgetEventIana(eventIana);
  const utcSegs = campaign.budget.segments.map((s) => ({
    ...s,
    time_start: scheduleInputToUtcIso(s.time_start, tz) ?? s.time_start,
    time_end: scheduleInputToUtcIso(s.time_end, tz) ?? s.time_end,
  }));
  const flight = snapUtcFlightIsoBoundsToMetaBudgetScheduleGrid(bounds);
  const segments = normalizeScheduledBudgetSegmentsToEventDayJunctions({
    segments: utcSegs,
    eventIana: tz,
    flightStartIso: flight.flightStartIso,
    flightEndIso: flight.flightEndIso,
  });
  return {
    ...campaign,
    budget: { type: "scheduled", segments },
  };
}

/** 15m UTC grid: floor start, ceil end; widen by one quantum if snap collapses the range. */
export function snapMetaBudgetScheduleWindowUnixRange(params: {
  time_start: number;
  time_end: number;
}): { time_start: number; time_end: number } {
  const q = META_BUDGET_SCHEDULE_INTERVAL_SEC;
  const time_start = snapUnixSecondsToMetaBudgetScheduleUtc(
    params.time_start,
    "floor"
  );
  let time_end = snapUnixSecondsToMetaBudgetScheduleUtc(
    params.time_end,
    "ceil"
  );
  if (time_end <= time_start) {
    time_end = time_start + q;
  }
  return { time_start, time_end };
}

function shiftSortedSnappedBudgetWindowsNonOverlapping<
  T extends { time_start: number; time_end: number },
>(out: T[], quantumSeconds: number): void {
  for (let i = 1; i < out.length; i++) {
    if (out[i].time_start < out[i - 1].time_end) {
      out[i].time_start = out[i - 1].time_end;
    }
    if (out[i].time_end <= out[i].time_start) {
      out[i].time_end = out[i].time_start + quantumSeconds;
    }
  }
}

/**
 * Per-window 15m snap uses **`floor` on starts** (see {@link snapMetaBudgetScheduleWindowUnixRange})
 * and **`ceil` on ends**. Adjacent boosted windows can still overlap after snap if boundaries
 * are awkward; this routine sorts and shifts later starts to the previous row’s `time_end`.
 *
 * Meta rejects overlapping high-demand periods (`error_subcode` 3858167).
 */
export function normalizeSnappedMetaBudgetScheduleWindowsNonOverlapping<
  T extends { time_start: number; time_end: number },
>(rows: T[]): T[] {
  if (rows.length <= 1) return rows;
  const q = META_BUDGET_SCHEDULE_INTERVAL_SEC;
  const sorted = [...rows].sort((a, b) =>
    a.time_start !== b.time_start
      ? a.time_start - b.time_start
      : a.time_end - b.time_end
  );
  const out = sorted.map((r) => ({ ...r }));
  shiftSortedSnappedBudgetWindowsNonOverlapping(out, q);
  return out;
}

/** @deprecated Use {@link META_BUDGET_SCHEDULE_MAX_SEGMENTS}. */
export const META_BUDGET_SCHEDULE_MAX_WINDOWS =
  META_BUDGET_SCHEDULE_MAX_SEGMENTS;

export type CampaignBudgetWindowsValidationErrorCode =
  | "NO_SEGMENTS"
  | "TOO_FEW_SEGMENTS"
  | "NO_WINDOWS"
  | "WINDOW_INVALID_RANGE"
  | "TOO_MANY_SEGMENTS"
  | "TOO_MANY_WINDOWS"
  | "INVALID_ISO_TIME"
  | "TIMELINE_GAP"
  | "BOUNDARY_INVALID"
  | "OUTSIDE_FLIGHT_START"
  | "OUTSIDE_FLIGHT_END"
  | "FLIGHT_START_MISMATCH"
  | "FLIGHT_END_MISMATCH"
  | "PHASE_TOO_SHORT";

export type CampaignBudgetWindowsValidationError = {
  code: CampaignBudgetWindowsValidationErrorCode;
  message: string;
};

/** Base slice for Meta when the campaign uses a simple daily/lifetime budget. */
export function metaBaseBudgetFromSimpleCampaignBudget(
  budget: MarketingCampaignBudgetSimple
): MarketingCampaignBudgetSimple {
  return budget;
}

/**
 * Derives Meta base (min segment **daily-equivalent**) + optional `budget_schedules` rows from phased segments.
 *
 * KYD segment `amount` is the **phase total** (minor units), not a per-day budget. Meta needs a daily cap,
 * so each phase maps to **`amount / max(day_units, 1)`** where `day_units` comes from
 * {@link scheduledSegmentElapsedDayUnits} in **`timezone`**. Long phases divide by real duration
 * ($60 over 2 days → $30/day). Sub-day phases use the **full phase total as one day’s cap**
 * (divide by 1, not by 3/24) so we do not inflate daily rates; Meta prorates spend in short windows.
 *
 * Meta campaign `daily_budget` is the **minimum** daily-equivalent across phases. For each higher phase,
 * Graph `budget_schedules` with `budget_value_type: ABSOLUTE` is the **increment** above that base:
 * effective cap for the window ≈ `daily_budget + budget_value`.
 */
export function phasedSegmentsToMetaBaseAndScheduleRows(
  budget: MarketingCampaignBudgetScheduled,
  timezone?: string
): {
  base: MarketingCampaignBudgetSimple;
  scheduleRows: MarketingCampaignBudgetScheduleWindow[];
  /** `scheduleRows[i]` was derived from `budget.segments[scheduleRowSourceSegmentIndices[i]]`. */
  scheduleRowSourceSegmentIndices: number[];
} {
  const { segments } = budget;
  if (!segments.length) {
    throw new Error("phasedSegmentsToMetaBaseAndScheduleRows: empty segments");
  }

  const segmentDailyMinorRates: Array<{
    sourceSegmentIndex: number;
    time_start: string;
    time_end: string;
    dailyAmount: number;
  }> = [];
  let minDailyMinor = Infinity;
  let minCurrency = segments[0].amount.currency ?? "USD";

  for (let sourceSegmentIndex = 0; sourceSegmentIndex < segments.length; sourceSegmentIndex++) {
    const s = segments[sourceSegmentIndex]!;
    const amt = coerceFiniteMinorAmount(s.amount?.amount);
    if (amt == null || amt <= 0) continue;
    const elapsedDays = scheduledSegmentElapsedDayUnits(
      s.time_start,
      s.time_end,
      timezone
    );
    if (!Number.isFinite(elapsedDays) || elapsedDays <= 0) {
      continue;
    }
    const pacingDayUnits = Math.max(elapsedDays, 1);
    const dailyAmount = amt / pacingDayUnits;
    if (!Number.isFinite(dailyAmount) || dailyAmount <= 0) {
      continue;
    }
    segmentDailyMinorRates.push({
      sourceSegmentIndex,
      time_start: s.time_start,
      time_end: s.time_end,
      dailyAmount: dailyAmount,
    });
    if (dailyAmount < minDailyMinor) {
      minDailyMinor = dailyAmount;
      minCurrency = s.amount.currency ?? minCurrency;
    }
  }

  if (!Number.isFinite(minDailyMinor) || minDailyMinor <= 0) {
    throw new Error(
      "phasedSegmentsToMetaBaseAndScheduleRows: could not resolve min segment daily-equivalent amount"
    );
  }
  const baseMinorRounded = Math.max(1, Math.floor(minDailyMinor));
  const base: MarketingCampaignBudgetSimple = {
    type: "daily",
    amount: minorAmountToPrice(baseMinorRounded, minCurrency),
  };
  const scheduleRows: MarketingCampaignBudgetScheduleWindow[] = [];
  const scheduleRowSourceSegmentIndices: number[] = [];
  for (const seg of segmentDailyMinorRates) {
    const segmentMinorRounded = Math.max(1, Math.floor(seg.dailyAmount));
    const incrementMinor = segmentMinorRounded - baseMinorRounded;
    if (incrementMinor <= 0) continue;
    scheduleRows.push({
      time_start: seg.time_start,
      time_end: seg.time_end,
      budget_value: incrementMinor,
      budget_value_type: "ABSOLUTE",
    });
    scheduleRowSourceSegmentIndices.push(seg.sourceSegmentIndex);
  }
  return { base, scheduleRows, scheduleRowSourceSegmentIndices };
}

/**
 * Meta Graph `spend_cap` (minor units) for campaign create/update: total spend ceiling aligned with
 * KYD “total budget” semantics over the combined ad-set flight window.
 *
 * - **Daily:** `spend_cap` = **per-day budget (minor)** × **inclusive UTC calendar-day count** of the
 *   combined ad-set flight ({@link deriveCampaignFlightIsoBoundsFromAdsets} →
 *   {@link scheduleInclusiveUtcDayCountFromIso}). Same product as
 *   {@link getCampaignBudgetMinorAmountForDisplay} when given that day count — not a separately
 *   stored “total budget” field.
 * - **Lifetime:** always `undefined`. The campaign node uses `lifetime_budget` only; Meta rejects or
 *   misbehaves when `spend_cap` duplicates that ceiling.
 * - **Scheduled:** sum of segment amounts (total planned spend across phases).
 *
 * **Scheduled:** sums segment minors via {@link displayMinorTotalScheduled} (coerced amounts)
 * whenever segments exist and the sum is positive — **without** requiring
 * {@link hasCampaignBudgetForMetaPublish}, so create/update still send `spend_cap` when Dynamo/API
 * uses numeric strings or the graph wire is not yet derivable.
 *
 * **Scheduled Meta wire:** per-phase daily-equivalent follows {@link phasedSegmentsToMetaBaseAndScheduleRows}
 * (phase total ÷ duration in day units).
 *
 * **Daily / lifetime:** still require {@link hasCampaignBudgetForMetaPublish} for **daily** (then
 * returns `undefined` when incomplete or flight bounds are missing). **Lifetime** skips the cap
 * entirely as above.
 */
export function metaCampaignSpendCapMinorForPublish(params: {
  budget: MarketingCampaignBudget | undefined;
  adsets: Array<{
    schedule?: { start_time?: string | null; stop_time?: string | null } | null;
  }>;
}): number | undefined {
  const { budget, adsets } = params;
  if (!budget) {
    return undefined;
  }
  if (budget.type === "scheduled") {
    const total = displayMinorTotalScheduled(budget);
    return total != null && total > 0 ? Math.round(total) : undefined;
  }
  if (!hasCampaignBudgetForMetaPublish(budget)) {
    return undefined;
  }
  if (budget.type === "daily") {
    const dailyBudgetMinor = coerceFiniteMinorAmount(budget.amount?.amount);
    if (dailyBudgetMinor == null || dailyBudgetMinor <= 0) return undefined;
    const bounds = deriveCampaignFlightIsoBoundsFromAdsets(adsets ?? []);
    const inclusiveFlightDayCount = bounds
      ? scheduleInclusiveUtcDayCountFromIso(
          bounds.flightStartIso,
          bounds.flightEndIso
        )
      : null;
    if (inclusiveFlightDayCount == null || inclusiveFlightDayCount < 1) {
      return undefined;
    }
    return Math.round(dailyBudgetMinor * inclusiveFlightDayCount);
  }
  if (budget.type === "lifetime") {
    return undefined;
  }
  return undefined;
}

/**
 * Graph `daily_budget` / `lifetime_budget` for campaign create/update, plus derived high-demand
 * windows when applicable (POST each window to `/{campaign_id}/budget_schedules` after create/update).
 *
 * Meta requires **daily_budget** (not `lifetime_budget`) when the campaign uses high-demand
 * budget scheduling (OAuth `error_subcode` 3858199 pairs daily budget with the `budget_schedules` edge).
 * Scheduled budgets with at least one boosted row therefore use `daily_budget` only
 * (minimum daily-equivalent across segments); scheduled budgets with no boosted rows also use
 * `daily_budget` since the derived base is a per-day amount.
 * Each `scheduleRows[].budget_value` with `ABSOLUTE` is the **delta** above that `daily_budget` for the window.
 *
 * When `budget.type === "scheduled"`, pass **`timezone`** (typically first ad-set
 * `schedule.timezone` via {@link timezoneFromCampaignAdsets}) so phased daily-equivalent uses the same
 * calendar-day model as {@link scheduledSegmentElapsedDayUnits}.
 */
export function metaCampaignBudgetWireForGraph(
  budget: MarketingCampaignBudget | undefined,
  timezone?: string
): {
  daily_budget?: number;
  lifetime_budget?: number;
  scheduleRows: MarketingCampaignBudgetScheduleWindow[];
} {
  if (!budget) {
    return { scheduleRows: [] };
  }
  if (budget.type === "scheduled" && (budget.segments ?? []).length > 0) {
    const derived = phasedSegmentsToMetaBaseAndScheduleRows(
      budget,
      timezone
    );
    const amt = derived.base.amount?.amount;
    const n = typeof amt === "number" && Number.isFinite(amt) ? amt : undefined;
    return {
      ...(n != null ? { daily_budget: n } : {}),
      scheduleRows: derived.scheduleRows,
    };
  }
  if (budget.type === "daily") {
    const amt = budget.amount?.amount;
    const n = typeof amt === "number" && Number.isFinite(amt) ? amt : undefined;
    return { ...(n != null ? { daily_budget: n } : {}), scheduleRows: [] };
  }
  if (budget.type === "lifetime") {
    const amt = budget.amount?.amount;
    const n = typeof amt === "number" && Number.isFinite(amt) ? amt : undefined;
    return { ...(n != null ? { lifetime_budget: n } : {}), scheduleRows: [] };
  }
  return { scheduleRows: [] };
}

/** Base slice for Meta when the campaign uses phased scheduled budget (min daily-equivalent segment). */
export function metaBaseBudgetFromScheduledCampaignBudget(
  budget: MarketingCampaignBudgetScheduled,
  timezone?: string
): MarketingCampaignBudgetSimple {
  return phasedSegmentsToMetaBaseAndScheduleRows(
    budget,
    timezone
  ).base;
}

/**
 * Numbers derived from a KYD campaign budget for Meta’s **campaign** node (`daily_budget` /
 * `lifetime_budget`, minor units) plus optional **high-demand** windows (`scheduleRows`) that are
 * sent in a second step to `POST /{campaign_id}/budget_schedules`.
 *
 * This is what {@link metaCampaignBudgetWireForGraph} computes; the handle exposes the same shape
 * as {@link CampaignBudgetHandle.metaGraphBudgetFields}.
 */
export type MetaCampaignGraphBudgetFields = ReturnType<
  typeof metaCampaignBudgetWireForGraph
>;

/** @deprecated Renamed to {@link MetaCampaignGraphBudgetFields}. */
export type MetaCampaignBudgetWire = MetaCampaignGraphBudgetFields;

/**
 * How the budget is stored in KYD (not Meta’s vocabulary):
 *
 * - **`none`**: no `campaign.budget` (or empty handling).
 * - **`simple`**: a normal **daily or lifetime** budget — one `amount` on the campaign. In KYD
 *   this is `MarketingCampaignBudget` with `type: "daily" | "lifetime"`.
 * - **`scheduled`**: a **phased** budget — many time-bounded **segments**, each with its own amount.
 *   In KYD this is `type: "scheduled"`. Meta still needs one “base” daily/lifetime pair on the
 *   campaign (we use the minimum segment) plus separate rows for boosted/high-demand windows.
 *
 * Most callers should **not** branch on `tag`: use {@link CampaignBudgetHandle.metaGraphBudgetFields},
 * {@link CampaignBudgetHandle.baseForMeta}, {@link CampaignBudgetHandle.displayMinorTotal}, and
 * the `needs*` helpers. Use {@link CampaignBudgetHandle.fold} only when you must produce different
 * **KYD-shaped** objects (e.g. scaling every segment vs scaling one amount).
 */
export type CampaignBudgetTag = "none" | "simple" | "scheduled";

/** Optional inputs that affect phased scheduled → Meta mapping (see {@link campaignBudgetHandle}). */
export type CampaignBudgetHandleOptions = {
  /**
   * IANA timezone id for phased Meta daily-equivalent (see {@link scheduledSegmentElapsedDayUnits}).
   * Use {@link timezoneFromCampaignAdsets} when publishing a full campaign.
   */
  readonly timezone?: string;
};

/**
 * Single entry point for campaign budget behavior (simple vs scheduled vs missing).
 * Prefer this over scattered `budget.type === "scheduled"` checks at call sites.
 */
export interface CampaignBudgetHandle {
  readonly tag: CampaignBudgetTag;

  /**
   * Budget numbers Meta expects on the **campaign** create/update form, plus any derived
   * high-demand windows for the `budget_schedules` edge. Not a network call — pure mapping from KYD
   * → Meta field names.
   */
  metaGraphBudgetFields(): MetaCampaignGraphBudgetFields;

  /** True when boosted schedule rows exist and must be POSTed to `/{id}/budget_schedules`. */
  needsMetaBudgetScheduleEdge(): boolean;

  /** True when campaign create/update should send `LOWEST_COST_WITHOUT_CAP` for budget fields. */
  needsLowestCostWithoutCapBidStrategy(): boolean;

  /** Min-segment base for Meta `daily_budget` / `lifetime_budget` (scheduled) or the simple budget. */
  baseForMeta(): MarketingCampaignBudgetSimple | undefined;

  /** Sum of segment minors (scheduled) or single amount (simple); for UI / aggregates. */
  displayMinorTotal(): number | null;

  isCompleteForMetaPublish(): boolean;

  /** Meta `budget_schedules` Graph ids on boosted segments (`external_id`). */
  storedMetaScheduleGraphIds(): string[];

  fold<R>(arms: {
    none: () => R;
    simple: (b: MarketingCampaignBudgetSimple) => R;
    scheduled: (b: MarketingCampaignBudgetScheduled) => R;
  }): R;
}

function storedMetaScheduleGraphIdsFromScheduled(
  budget: MarketingCampaignBudgetScheduled
): string[] {
  const out: string[] = [];
  for (const seg of budget.segments ?? []) {
    const sid = seg.external_id?.trim();
    if (sid) out.push(sid);
  }
  return [...new Set(out)];
}

/** Minor-unit amount from API/JSON that may use number or numeric string. */
function coerceFiniteMinorAmount(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const t = value.trim();
    if (t === "") return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function displayMinorTotalScheduled(
  budget: MarketingCampaignBudgetScheduled
): number | null {
  const segs = budget.segments ?? [];
  if (segs.length === 0) return null;
  let sumMinor = 0;
  let sawValid = false;
  for (const s of segs) {
    const a = coerceFiniteMinorAmount(s.amount?.amount);
    if (a != null && a >= 0) {
      sumMinor += a;
      sawValid = true;
    }
  }
  return sawValid ? sumMinor : null;
}

function isCompleteScheduledForMetaPublish(
  budget: MarketingCampaignBudgetScheduled
): boolean {
  const segs = budget.segments ?? [];
  if (segs.length === 0) return false;
  return segs.every((s) => {
    const a = coerceFiniteMinorAmount(s.amount?.amount);
    return a != null && a > 0;
  });
}

export function campaignBudgetHandle(
  budget: MarketingCampaignBudget | undefined,
  options?: CampaignBudgetHandleOptions
): CampaignBudgetHandle {
  if (!budget) {
    return {
      tag: "none",
      metaGraphBudgetFields: () => ({ scheduleRows: [] }),
      needsMetaBudgetScheduleEdge: () => false,
      needsLowestCostWithoutCapBidStrategy: () => false,
      baseForMeta: () => undefined,
      displayMinorTotal: () => null,
      isCompleteForMetaPublish: () => false,
      storedMetaScheduleGraphIds: () => [],
      fold: (arms) => arms.none(),
    };
  }

  if (budget.type === "scheduled") {
    const b = budget;
    const timezone = options?.timezone;
    let memo: MetaCampaignGraphBudgetFields | undefined;
    const getMetaGraphBudgetFields = (): MetaCampaignGraphBudgetFields =>
      (memo ??= metaCampaignBudgetWireForGraph(b, timezone));

    return {
      tag: "scheduled",
      metaGraphBudgetFields: () => getMetaGraphBudgetFields(),
      needsMetaBudgetScheduleEdge: () =>
        getMetaGraphBudgetFields().scheduleRows.length > 0,
      needsLowestCostWithoutCapBidStrategy: () => {
        const w = getMetaGraphBudgetFields();
        return (
          w.daily_budget != null ||
          w.lifetime_budget != null ||
          w.scheduleRows.length > 0
        );
      },
      baseForMeta: () =>
        metaBaseBudgetFromScheduledCampaignBudget(b, timezone),
      displayMinorTotal: () => displayMinorTotalScheduled(b),
      isCompleteForMetaPublish: () => isCompleteScheduledForMetaPublish(b),
      storedMetaScheduleGraphIds: () =>
        storedMetaScheduleGraphIdsFromScheduled(b),
      fold: (arms) => arms.scheduled(b),
    };
  }

  const b = budget;
  return {
    tag: "simple",
    metaGraphBudgetFields: () => metaCampaignBudgetWireForGraph(b),
    needsMetaBudgetScheduleEdge: () => false,
    needsLowestCostWithoutCapBidStrategy: () => {
      const w = metaCampaignBudgetWireForGraph(b);
      return w.daily_budget != null || w.lifetime_budget != null;
    },
    baseForMeta: () => metaBaseBudgetFromSimpleCampaignBudget(b),
    displayMinorTotal: () => coerceFiniteMinorAmount(b.amount?.amount),
    isCompleteForMetaPublish: () => {
      const base = metaBaseBudgetFromSimpleCampaignBudget(b);
      return (
        !!base &&
        typeof base.amount?.amount === "number" &&
        (base.type === "daily" || base.type === "lifetime")
      );
    },
    storedMetaScheduleGraphIds: () => [],
    fold: (arms) => arms.simple(b),
  };
}

/**
 * Base daily/lifetime slice used for Meta `daily_budget` / `lifetime_budget`
 * (and bid_strategy) for both simple and scheduled campaign budgets.
 */
export function getCampaignBudgetBaseForMeta(
  budget: MarketingCampaignBudget | undefined,
  options?: CampaignBudgetHandleOptions
): MarketingCampaignBudgetSimple | undefined {
  return campaignBudgetHandle(budget, options).baseForMeta();
}

export type CampaignBudgetDisplayMinorOptions = {
  /**
   * When set with a **daily** campaign budget, the returned minor total is
   * `displayMinorTotal() × scheduleInclusiveDayCount`. Ignored for `lifetime` and `scheduled`.
   */
  scheduleInclusiveDayCount?: number | null;
};

/**
 * Currency string for formatting a budget’s display minor (first segment currency for scheduled,
 * otherwise `amount.currency` on simple budgets).
 */
export function getCampaignBudgetDisplayCurrency(
  budget: MarketingCampaignBudget | MarketingCampaignBudgetSimple | undefined
): string | undefined {
  if (!budget) return undefined;
  if (budget.type === "scheduled") {
    return budget.segments?.find((s) => s.amount?.currency)?.amount?.currency;
  }
  return budget.amount?.currency;
}

/**
 * Minor-unit amount for UI labels: simple `amount.amount`, or **sum of all segment amounts**
 * for scheduled budgets (total planned spend over the campaign lifetime). This intentionally
 * differs from {@link getCampaignBudgetBaseForMeta}, which uses the minimum segment for Meta’s
 * base `daily_budget` / `lifetime_budget` wire format.
 *
 * For **`daily`** budgets only, {@link CampaignBudgetDisplayMinorOptions.scheduleInclusiveDayCount}
 * multiplies that per-day minor into an estimated run total (e.g. Algolia list “total budget”).
 */
export function getCampaignBudgetMinorAmountForDisplay(
  budget: MarketingCampaignBudget | undefined,
  options?: CampaignBudgetDisplayMinorOptions
): number | null {
  const baseMinor = campaignBudgetHandle(budget).displayMinorTotal();
  if (baseMinor == null) return null;
  const days = options?.scheduleInclusiveDayCount;
  if (
    budget?.type === "daily" &&
    typeof days === "number" &&
    Number.isFinite(days) &&
    days >= 1
  ) {
    return Math.round(baseMinor * days);
  }
  return baseMinor;
}

/**
 * Total planned budget in minor units for UI: campaign-level budget (including sum of
 * scheduled phases) when present and valid, otherwise sum of ad set budgets.
 */
export function getCampaignAggregatedBudgetAmountMinor(
  campaign: Pick<MarketingCampaign, "budget" | "adsets">
): number | null {
  if (campaign.budget) {
    const fromRoot = getCampaignBudgetMinorAmountForDisplay(campaign.budget);
    if (fromRoot != null) return fromRoot;
  }
  const adsets = campaign.adsets ?? [];
  let total = 0;
  for (const adset of adsets) {
    const a = coerceFiniteMinorAmount(adset.budget?.amount?.amount);
    if (a != null) total += a;
  }
  return total > 0 ? total : null;
}

export function hasCampaignBudgetForMetaPublish(
  budget: MarketingCampaignBudget | undefined
): budget is MarketingCampaignBudget {
  if (!budget) return false;
  return campaignBudgetHandle(budget).isCompleteForMetaPublish();
}

function parseIsoToUnixSeconds(iso: string): number | null {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return Math.floor(t / 1000);
}

/**
 * Inclusive count of **UTC calendar** days between two ISO instants (start/end of ad run).
 * Used with {@link getCampaignBudgetMinorAmountForDisplay} for **daily** budgets to estimate
 * total spend over the scheduled window. Returns `null` if bounds are missing/invalid or end
 * is before start.
 */
export function scheduleInclusiveUtcDayCountFromIso(
  scheduleStart?: string,
  scheduleEnd?: string
): number | null {
  if (!scheduleStart || !scheduleEnd) return null;
  const startMs = Date.parse(scheduleStart);
  const endMs = Date.parse(scheduleEnd);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
  if (endMs < startMs) return null;
  const start = new Date(startMs);
  const end = new Date(endMs);
  const startUtc = Date.UTC(
    start.getUTCFullYear(),
    start.getUTCMonth(),
    start.getUTCDate()
  );
  const endUtc = Date.UTC(
    end.getUTCFullYear(),
    end.getUTCMonth(),
    end.getUTCDate()
  );
  const calendarDaySpan = Math.floor((endUtc - startUtc) / 86_400_000) + 1;
  return Math.max(1, calendarDaySpan);
}

/** One **local calendar day** in `eventIana` overlapping `[time_start, time_end)` (hours). */
export type CalendarDayHoursSlice = {
  /** Local calendar date `YYYY-MM-DD` in `eventIana`. */
  dateLocal: string;
  /** Hours of `[time_start, time_end)` overlapping this local calendar day. */
  hours: number;
};

/**
 * Unix ms of the next local midnight strictly after `cursorMs`.
 * Built from the calendar date: `startOf("day").add(1, "day")` on a zoned instant keeps the
 * pre-fallback UTC offset, so the same unix time is returned and the caller never advances.
 */
function nextLocalMidnightMs(cursorMs: number, tz: string): number {
  const dateLocal = dayjs(cursorMs).tz(tz).format("YYYY-MM-DD");
  const nextMs = dayjs.tz(dateLocal, tz).add(1, "day").startOf("day").valueOf();
  if (nextMs > cursorMs) return nextMs;
  throw new Error(
    `nextLocalMidnightMs: local day did not advance at ${dateLocal} (${tz})`
  );
}

/**
 * Splits `[startIso, endIso)` into **local calendar-day** slices in `eventIana` and returns overlap
 * **hours** per day. Partial first/last days use real instants (e.g. 17th 14:00→local midnight =
 * **10h** when the slice is 10h long). A DST fallback day is 25h; a spring-forward day is 23h.
 */
export function calendarDayHoursInIsoRange(
  startIso: string,
  endIso: string,
  eventIana: string
): CalendarDayHoursSlice[] {
  const tz = resolveScheduledBudgetEventIana(eventIana);
  const startMs = Date.parse(startIso.trim());
  const endMs = Date.parse(endIso.trim());
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    return [];
  }
  const out: CalendarDayHoursSlice[] = [];
  let cursor = startMs;
  while (cursor < endMs) {
    const dateLocal = dayjs(cursor).tz(tz).format("YYYY-MM-DD");
    const nextMidnightMs = nextLocalMidnightMs(cursor, tz);
    const segmentEnd = Math.min(endMs, nextMidnightMs);
    const hours = (segmentEnd - cursor) / 3_600_000;
    out.push({ dateLocal, hours });
    cursor = segmentEnd;
  }
  return out;
}

/**
 * **Day-units** for a scheduled phase: `sum_k (hours_k / 24)` over local calendar days in
 * `timezone` between ISO bounds. For a single continuous `[start,end)` interval this
 * sum equals `(time_end − time_start) / 24h` in absolute time (partitioning by local midnights does
 * not change total hours). When `timezone` is omitted or `UTC`, returns that ratio
 * directly so existing call sites and tests stay bit-stable.
 */
export function scheduledSegmentElapsedDayUnits(
  startIso: string,
  endIso: string,
  timezone?: string
): number {
  const startMs = Date.parse(startIso.trim());
  const endMs = Date.parse(endIso.trim());
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    return NaN;
  }
  const raw = (endMs - startMs) / 86_400_000;
  const zone = (timezone ?? "").trim();
  if (!zone || zone === "UTC") {
    return raw;
  }
  let u = 0;
  for (const s of calendarDayHoursInIsoRange(startIso, endIso, zone)) {
    u += s.hours / 24;
  }
  return u;
}

/** First non-empty ad-set `schedule.timezone`; else `UTC` (same default as {@link resolveScheduledBudgetEventIana}). */
export function timezoneFromCampaignAdsets(
  adsets: MarketingCampaign["adsets"]
): string {
  for (const a of adsets ?? []) {
    const tz = a.schedule?.timezone?.trim();
    if (tz) return resolveScheduledBudgetEventIana(tz);
  }
  return "UTC";
}

/** One UTC calendar day’s overlap with `[time_start, time_end)` in hours (see {@link calendarDayHoursInIsoRange} with `UTC`). */
export type UtcCalendarDayHoursSlice = {
  /** UTC calendar date `YYYY-MM-DD` (the day key in UTC). */
  dateUtc: string;
  /** Hours of `[time_start, time_end)` overlapping this UTC calendar day. */
  hours: number;
};

/**
 * Splits `[startIso, endIso)` into **UTC calendar-day** slices and returns overlap **hours** per day.
 * Partial first/last days follow real instants (e.g. 17th 14:00→24:00 UTC = **10h**; 26th 00:00→18:00 = **18h**).
 */
export function utcCalendarDayHoursInIsoRange(
  startIso: string,
  endIso: string
): UtcCalendarDayHoursSlice[] {
  return calendarDayHoursInIsoRange(startIso, endIso, "UTC").map((s) => ({
    dateUtc: s.dateLocal,
    hours: s.hours,
  }));
}

function distributeMinorByPositiveWeights(
  totalMinor: number,
  weights: readonly number[]
): number[] {
  if (weights.length === 0) return [];
  const wsum = weights.reduce((a, b) => a + b, 0);
  if (!(wsum > 0) || !Number.isFinite(totalMinor) || totalMinor <= 0) {
    return weights.map(() => 0);
  }
  const raw = weights.map((w) => (totalMinor * w) / wsum);
  const floors = raw.map((x) => Math.floor(x));
  let rem = totalMinor - floors.reduce((a, b) => a + b, 0);
  const order = raw
    .map((x, i) => ({ i, frac: x - Math.floor(x) }))
    .sort((a, b) => b.frac - a.frac);
  const out = [...floors];
  for (const o of order) {
    if (rem <= 0) break;
    out[o.i] += 1;
    rem -= 1;
  }
  return out;
}

/**
 * Allocate **integer minor units** across **local** calendar days in `[startIso, endIso)` in
 * `eventIana`, proportionally to {@link calendarDayHoursInIsoRange} hour weights. Sums to `totalMinor`.
 */
export function allocateMinorProportionalByCalendarDayHours(params: {
  startIso: string;
  endIso: string;
  totalMinor: number;
  eventIana: string;
}): ReadonlyArray<{ dateLocal: string; minor: number }> {
  const slices = calendarDayHoursInIsoRange(
    params.startIso,
    params.endIso,
    params.eventIana
  );
  const weights = slices.map((s) => s.hours);
  const minors = distributeMinorByPositiveWeights(params.totalMinor, weights);
  return slices.map((s, i) => ({ dateLocal: s.dateLocal, minor: minors[i] ?? 0 }));
}

/**
 * Allocate **integer minor units** across UTC calendar days (same as
 * {@link allocateMinorProportionalByCalendarDayHours} with `eventIana: "UTC"`).
 */
export function allocateMinorProportionalByUtcCalendarDayHours(params: {
  startIso: string;
  endIso: string;
  totalMinor: number;
}): ReadonlyArray<{ dateUtc: string; minor: number }> {
  const rows = allocateMinorProportionalByCalendarDayHours({
    ...params,
    eventIana: "UTC",
  });
  return rows.map((r) => ({ dateUtc: r.dateLocal, minor: r.minor }));
}

/**
 * Validates phased budget segments on persisted UTC ISO rows (launch, publish, guided-edit persist).
 */
export function validateBudgetSegments(params: {
  segments: MarketingCampaignBudgetSegment[];
  requireAtLeastOne: boolean;
  /**
   * Product minimum row count (defaults to 1 when `requireAtLeastOne`, else 0).
   * Scheduled campaign validation passes {@link SCHEDULED_BUDGET_MIN_SEGMENTS}.
   */
  minSegmentCount?: number;
  /** When true, each segment's `time_end` must equal the next segment's `time_start` (ISO equality). */
  requireContiguous?: boolean;
}): CampaignBudgetWindowsValidationError | null {
  const { segments, requireAtLeastOne, requireContiguous = true } = params;
  const minCount =
    params.minSegmentCount ?? (requireAtLeastOne ? 1 : 0);
  if (segments.length === 0 && minCount > 0) {
    if (minCount >= SCHEDULED_BUDGET_MIN_SEGMENTS) {
      return {
        code: "TOO_FEW_SEGMENTS",
        message: SCHEDULED_BUDGET_MIN_SEGMENTS_MESSAGE,
      };
    }
    return {
      code: "NO_SEGMENTS",
      message: "Scheduled campaign budget requires at least one phase.",
    };
  }
  if (segments.length < minCount) {
    if (minCount >= SCHEDULED_BUDGET_MIN_SEGMENTS) {
      return {
        code: "TOO_FEW_SEGMENTS",
        message: SCHEDULED_BUDGET_MIN_SEGMENTS_MESSAGE,
      };
    }
  }
  if (segments.length > SCHEDULED_BUDGET_MAX_SEGMENTS) {
    return {
      code: "TOO_MANY_SEGMENTS",
      message: `At most ${SCHEDULED_BUDGET_MAX_SEGMENTS} budget phases are allowed.`,
    };
  }
  for (let i = 0; i < segments.length; i++) {
    const s = segments[i];
    const start = parseIsoToUnixSeconds(s.time_start);
    const end = parseIsoToUnixSeconds(s.time_end);
    if (start == null || end == null) {
      return {
        code: "INVALID_ISO_TIME",
        message: "Each phase must use valid ISO 8601 time_start and time_end.",
      };
    }
    if (end <= start) {
      return {
        code: "WINDOW_INVALID_RANGE",
        message: "Each phase must have time_end after time_start.",
      };
    }
    if (end - start < SCHEDULED_BUDGET_PHASE_MIN_DURATION_SECONDS) {
      return {
        code: "PHASE_TOO_SHORT",
        message: SCHEDULED_BUDGET_PHASE_MIN_DURATION_MESSAGE,
      };
    }
    const amt = s.amount?.amount;
    if (typeof amt !== "number" || !Number.isFinite(amt) || amt <= 0) {
      return {
        code: "WINDOW_INVALID_RANGE",
        message: "Each phase must have a positive amount.",
      };
    }
    if (requireContiguous && i < segments.length - 1) {
      const next = segments[i + 1];
      const endMs = Date.parse(s.time_end);
      const nextStartMs = Date.parse(next.time_start);
      if (
        !Number.isFinite(endMs) ||
        !Number.isFinite(nextStartMs) ||
        endMs !== nextStartMs
      ) {
        return {
          code: "TIMELINE_GAP",
          message:
            "Phases must be contiguous: each phase end must be the same instant as the next phase start.",
        };
      }
    }
  }
  return null;
}

/**
 * Each row’s `[time_start, time_end]` must lie inside `[flightStart, flightEnd]` **inclusive** on
 * the Unix timeline. Does not compare to `Date.now()` — a window may be as early as the flight
 * start (even if that instant is in the past).
 */
function budgetIsoRowsWithinFlightInclusive(params: {
  rows: Array<{ time_start: string; time_end: string }>;
  flightStart: number;
  flightEnd: number;
  invalidIsoMessage: string;
  beforeFlightMessage: string;
  afterFlightMessage: string;
}): CampaignBudgetWindowsValidationError | null {
  for (const row of params.rows) {
    const rowStart = parseIsoToUnixSeconds(row.time_start);
    const rowEnd = parseIsoToUnixSeconds(row.time_end);
    if (rowStart == null || rowEnd == null) {
      return {
        code: "INVALID_ISO_TIME",
        message: params.invalidIsoMessage,
      };
    }
    if (rowStart < params.flightStart) {
      return {
        code: "OUTSIDE_FLIGHT_START",
        message: params.beforeFlightMessage,
      };
    }
    if (rowEnd > params.flightEnd) {
      return {
        code: "OUTSIDE_FLIGHT_END",
        message: params.afterFlightMessage,
      };
    }
  }
  return null;
}

/**
 * Validates scheduled-budget windows (derived Meta rows before POST).
 *
 * When {@link validateCampaignBudgetWindowsParams.flightStartIso} and `flightEndIso` are both set,
 * each window must lie inside that ad run (inclusive). Past vs “now” is not checked.
 */
export type ValidateCampaignBudgetWindowsParams = {
  windows: MarketingCampaignBudgetScheduleWindow[];
  requireAtLeastOne: boolean;
  flightStartIso?: string | null;
  flightEndIso?: string | null;
};

export function validateCampaignBudgetWindows(
  params: ValidateCampaignBudgetWindowsParams
): CampaignBudgetWindowsValidationError | null {
  const { windows, requireAtLeastOne, flightStartIso, flightEndIso } = params;
  if (requireAtLeastOne && windows.length === 0) {
    return {
      code: "NO_WINDOWS",
      message: "Scheduled campaign budget requires at least one schedule row.",
    };
  }
  if (windows.length > SCHEDULED_BUDGET_MAX_SEGMENTS) {
    return {
      code: "TOO_MANY_WINDOWS",
      message: `At most ${SCHEDULED_BUDGET_MAX_SEGMENTS} budget schedule rows are allowed.`,
    };
  }
  for (const w of windows) {
    const start = parseIsoToUnixSeconds(w.time_start);
    const end = parseIsoToUnixSeconds(w.time_end);
    if (start == null || end == null) {
      return {
        code: "INVALID_ISO_TIME",
        message: "Each window must use valid ISO 8601 time_start and time_end.",
      };
    }
    if (end <= start) {
      return {
        code: "WINDOW_INVALID_RANGE",
        message: "Each window must have time_end after time_start.",
      };
    }
    if (end - start < SCHEDULED_BUDGET_PHASE_MIN_DURATION_SECONDS) {
      return {
        code: "PHASE_TOO_SHORT",
        message: SCHEDULED_BUDGET_PHASE_MIN_DURATION_MESSAGE,
      };
    }
    if (w.budget_value <= 0 || !Number.isFinite(w.budget_value)) {
      return {
        code: "WINDOW_INVALID_RANGE",
        message: "Each window must have a positive budget_value.",
      };
    }
  }

  const fs = flightStartIso?.trim();
  const fe = flightEndIso?.trim();
  if (fs || fe) {
    if (!fs || !fe) {
      return {
        code: "BOUNDARY_INVALID",
        message:
          "Both flight start and end are required to validate budget windows against the ad run schedule.",
      };
    }
    const flightStart = parseIsoToUnixSeconds(fs);
    const flightEnd = parseIsoToUnixSeconds(fe);
    if (flightStart == null || flightEnd == null) {
      return {
        code: "BOUNDARY_INVALID",
        message: "Run schedule must use valid ISO 8601 start and end times.",
      };
    }
    if (flightEnd <= flightStart) {
      return {
        code: "BOUNDARY_INVALID",
        message: "Run schedule must end after it starts.",
      };
    }
    const inFlightErr = budgetIsoRowsWithinFlightInclusive({
      rows: windows,
      flightStart,
      flightEnd,
      invalidIsoMessage:
        "Each window must use valid ISO 8601 time_start and time_end.",
      beforeFlightMessage:
        "Budget schedule windows must not start before the ad run schedule.",
      afterFlightMessage:
        "Budget schedule windows must not end after the ad run schedule.",
    });
    if (inFlightErr) return inFlightErr;
  }

  return null;
}

/**
 * Maps KYD windows to Meta `budget_schedule_specs` on campaign create/update.
 * Meta requires `budget_value_type` ∈ { ABSOLUTE, MULTIPLIER } on this field (not PERCENT).
 */
export function budgetScheduleWindowsToMetaSpecs(
  windows: MarketingCampaignBudgetScheduleWindow[]
): Array<{
  time_start: number;
  time_end: number;
  budget_value: number;
  budget_value_type: "ABSOLUTE" | "MULTIPLIER";
  recurrence_type: "ONE_TIME";
}> {
  const snapped: Array<{
    time_start: number;
    time_end: number;
    budget_value: number;
    budget_value_type: "ABSOLUTE" | "MULTIPLIER";
    recurrence_type: "ONE_TIME";
  }> = windows.map((w) => {
    const rawStart = parseIsoToUnixSeconds(w.time_start);
    const rawEnd = parseIsoToUnixSeconds(w.time_end);
    if (rawStart == null || rawEnd == null) {
      throw new Error("Invalid ISO time in budget schedule window");
    }
    const { time_start, time_end } = snapMetaBudgetScheduleWindowUnixRange({
      time_start: rawStart,
      time_end: rawEnd,
    });
    return {
      time_start,
      time_end,
      budget_value: w.budget_value,
      budget_value_type: w.budget_value_type,
      recurrence_type: "ONE_TIME" as const,
    };
  });
  return normalizeSnappedMetaBudgetScheduleWindowsNonOverlapping(snapped);
}

/** Form fields for POST `/{campaign-id}/budget_schedules` (Meta allows ABSOLUTE | MULTIPLIER, not PERCENT). */
export function budgetScheduleWindowToEdgeFields(w: {
  time_start: number;
  time_end: number;
  budget_value: number;
  budget_value_type: "ABSOLUTE" | "MULTIPLIER";
}): Record<string, string> {
  return {
    time_start: String(w.time_start),
    time_end: String(w.time_end),
    budget_value: String(w.budget_value),
    budget_value_type: w.budget_value_type,
  };
}

/** Snapped + normalized payload for POST `/{id}/budget_schedules` (includes stable input row index). */
export type MetaBudgetScheduleEdgePayload = {
  time_start: number;
  time_end: number;
  budget_value: number;
  budget_value_type: "ABSOLUTE" | "MULTIPLIER";
  /** Index into the `windows` array passed to {@link budgetScheduleWindowsToEdgePayloads}. */
  scheduleRowIndex: number;
};

/**
 * Drop windows already ended before `nowSec`. For in-progress windows (started before `nowSec`,
 * end still in the future), optionally move `time_start` to the next Meta 15m UTC slot at or after now.
 */
function omitEndedWindowsOrAdvanceInProgressStart(params: {
  time_start: number;
  time_end: number;
  nowSec: number;
  /**
   * When false, keep the stored start for in-progress windows (linked campaign updates).
   * @see {@link BudgetScheduleWindowsToEdgePayloadsOptions.advanceInProgressWindowStartToNow}
   */
  advanceInProgressWindowStartToNow?: boolean;
}): { time_start: number; time_end: number } | null {
  const { nowSec } = params;
  if (params.time_end <= nowSec) {
    return null;
  }
  const snappedNowStart = snapUnixSecondsToMetaBudgetScheduleUtc(nowSec, "ceil");
  let time_start = params.time_start;
  const time_end = params.time_end;
  if (time_start < snappedNowStart) {
    if (params.advanceInProgressWindowStartToNow === false) {
      // Meta error_subcode 3858085: cannot POST a high-demand row whose start is in the past.
      return null;
    }
    time_start = snappedNowStart;
  }
  return { time_start, time_end };
}

export type BudgetScheduleWindowsToEdgePayloadsOptions = {
  /** Defaults to `Math.floor(Date.now() / 1000)`; set in tests for deterministic clamping. */
  nowUnixSeconds?: number;
  /**
   * When true (default), in-progress schedule rows (already started, not yet ended) have
   * `time_start` advanced to the next Meta 15m boundary at or after now before POST.
   * Set false only when building hypothetical payloads (e.g. sync-plan tests). Live POST must
   * advance (default); otherwise in-progress rows are omitted (Meta rejects past starts).
   */
  advanceInProgressWindowStartToNow?: boolean;
};

export function budgetScheduleWindowsToEdgePayloads(
  windows: MarketingCampaignBudgetScheduleWindow[],
  options?: BudgetScheduleWindowsToEdgePayloadsOptions
): MetaBudgetScheduleEdgePayload[] {
  const nowSec =
    options?.nowUnixSeconds ?? Math.floor(Date.now() / 1000);
  const built: MetaBudgetScheduleEdgePayload[] = [];
  for (let scheduleRowIndex = 0; scheduleRowIndex < windows.length; scheduleRowIndex++) {
    const w = windows[scheduleRowIndex]!;
    const parsedStart = parseIsoToUnixSeconds(w.time_start);
    const parsedEnd = parseIsoToUnixSeconds(w.time_end);
    if (parsedStart == null || parsedEnd == null) {
      throw new Error("Invalid ISO time in budget schedule window");
    }
    const publishWindow = omitEndedWindowsOrAdvanceInProgressStart({
      time_start: parsedStart,
      time_end: parsedEnd,
      nowSec,
      advanceInProgressWindowStartToNow:
        options?.advanceInProgressWindowStartToNow,
    });
    if (publishWindow == null) {
      continue;
    }
    const { time_start, time_end } = snapMetaBudgetScheduleWindowUnixRange({
      time_start: publishWindow.time_start,
      time_end: publishWindow.time_end,
    });
    built.push({
      time_start,
      time_end,
      budget_value: w.budget_value,
      budget_value_type: w.budget_value_type,
      scheduleRowIndex,
    });
  }
  return normalizeSnappedMetaBudgetScheduleWindowsNonOverlapping(built).filter(
    (row) =>
      row.time_end - row.time_start >= SCHEDULED_BUDGET_PHASE_MIN_DURATION_SECONDS
  );
}

/**
 * Half-open Unix windows `[time_start, time_end)` intersect with positive width.
 * A shared boundary (`a.time_end === b.time_start`) is **not** an overlap.
 */
export function metaBudgetScheduleUnixWindowsOverlap(
  a: { time_start: number; time_end: number },
  b: { time_start: number; time_end: number }
): boolean {
  return a.time_start < b.time_end && b.time_start < a.time_end;
}

/**
 * Pairwise overlaps among proposed edge payloads. Should be empty after
 * {@link normalizeSnappedMetaBudgetScheduleWindowsNonOverlapping}; if not, mapping is inconsistent.
 */
export function findMetaBudgetSchedulePayloadPairOverlaps<
  T extends { time_start: number; time_end: number },
>(rows: T[]): Array<{ i: number; j: number }> {
  const out: Array<{ i: number; j: number }> = [];
  for (let i = 0; i < rows.length; i++) {
    const ai = rows[i];
    if (!ai) continue;
    for (let j = i + 1; j < rows.length; j++) {
      const aj = rows[j];
      if (!aj) continue;
      if (metaBudgetScheduleUnixWindowsOverlap(ai, aj)) {
        out.push({ i, j });
      }
    }
  }
  return out;
}

/**
 * Each proposed row that intersects an existing Meta `budget_schedules` row (same half-open rule).
 */
export function findMetaBudgetScheduleNewVsExistingOverlaps<
  T extends { time_start: number; time_end: number },
>(
  newRows: T[],
  existing: Array<{ time_start: number; time_end: number; id: string }>
): Array<{ newIndex: number; existingId: string }> {
  const out: Array<{ newIndex: number; existingId: string }> = [];
  for (let ni = 0; ni < newRows.length; ni++) {
    const n = newRows[ni];
    if (!n) continue;
    for (const ex of existing) {
      if (metaBudgetScheduleUnixWindowsOverlap(n, ex)) {
        out.push({ newIndex: ni, existingId: ex.id });
      }
    }
  }
  return out;
}

/** Existing Meta row ids whose unix window overlaps `payload` (half-open rule). */
export function metaBudgetScheduleExistingIdsOverlappingPayload<
  T extends { time_start: number; time_end: number },
>(
  payload: T,
  existingMeta: Array<{ id: string; time_start: number; time_end: number }>
): string[] {
  return [
    ...new Set(
      findMetaBudgetScheduleNewVsExistingOverlaps([payload], existingMeta).map(
        (o) => o.existingId
      )
    ),
  ];
}

/** What {@link budgetScheduleWindowsToEdgePayloads} would POST after KYD→Meta mapping. */
export type MetaBudgetScheduleEdgeSyncPlan = {
  post: MetaBudgetScheduleEdgePayload[];
  /**
   * Existing Meta row ids superseded by POST (overlap). Included in
   * {@link deleteBeforePostMetaIds} and removed in the same single upfront batch — **before**
   * any POST, not per-row after success.
   */
  deleteMetaIds: string[];
  /**
   * Existing Meta rows still active (`time_end > now`) but no longer represented by any proposed
   * POST payload. These rows must be removed to avoid stale boosts when a segment falls back to
   * base daily budget (no schedule row).
   */
  deleteFutureOrInProgressMetaIds: string[];
  /**
   * All Meta row ids to remove before any POST ({@link deleteMetaIds} ∪
   * {@link deleteFutureOrInProgressMetaIds}, disjoint). Runtime deletes this set once, then POSTs
   * {@link post} — no per-row delete-after-POST or overlap-error retry.
   */
  deleteBeforePostMetaIds: string[];
  /** Existing Meta rows with no overlap vs proposed POST — left on the campaign. */
  keptMetaIds: string[];
};

/**
 * Pure plan for `postGraphCampaignBudgetSchedulesFromWindows`: which KYD windows become POST
 * payloads, which existing Meta rows each POST supersedes (overlap), and which past rows stay put.
 *
 * **Past vs future:** Ended phases are frozen in KYD for linked campaigns (not user-editable);
 * the planner still **omits** fully past windows from POST. Existing Meta rows for those phases
 * are **kept** unless a new POST window overlaps them. See `common/docs/meta-budget-schedule-sync.md`.
 */
/** Snapped `[time_start, time_end)` keys for boosted rows in the current KYD wire (not POST-filtered). */
function metaBudgetScheduleDesiredBoostedSnappedKeys(
  windows: MarketingCampaignBudgetScheduleWindow[]
): Set<string> {
  const keys = new Set<string>();
  for (const w of windows) {
    if (w.budget_value <= 0) continue;
    const key = metaBudgetScheduleSnappedKeyFromIsoSegment({
      time_start: w.time_start,
      time_end: w.time_end,
    });
    if (key) keys.add(key);
  }
  return keys;
}

export function planMetaBudgetScheduleEdgeSync(
  windows: MarketingCampaignBudgetScheduleWindow[],
  existingMeta: Array<{ id: string; time_start: number; time_end: number }>,
  options?: BudgetScheduleWindowsToEdgePayloadsOptions
): MetaBudgetScheduleEdgeSyncPlan {
  const post = budgetScheduleWindowsToEdgePayloads(windows, options);
  const nowSec =
    options?.nowUnixSeconds ?? Math.floor(Date.now() / 1000);
  const overlaps = findMetaBudgetScheduleNewVsExistingOverlaps(post, existingMeta);
  const deleteMetaIds = [...new Set(overlaps.map((o) => o.existingId))];
  const deleteSet = new Set(deleteMetaIds);
  const desiredBoostedKeys = metaBudgetScheduleDesiredBoostedSnappedKeys(windows);
  const deleteFutureOrInProgressMetaIds = existingMeta
    .filter((row) => row.time_end > nowSec && !deleteSet.has(row.id))
    .filter(
      (row) =>
        !desiredBoostedKeys.has(
          metaBudgetScheduleSnappedKeyFromUnixBounds(row)
        )
    )
    .map((row) => row.id);
  const staleSet = new Set(deleteFutureOrInProgressMetaIds);
  const keptMetaIds = existingMeta
    .filter((row) => !deleteSet.has(row.id) && !staleSet.has(row.id))
    .map((row) => row.id);
  const deleteBeforePostMetaIds = [
    ...new Set([...deleteMetaIds, ...deleteFutureOrInProgressMetaIds]),
  ];
  return {
    post,
    deleteMetaIds,
    deleteFutureOrInProgressMetaIds,
    deleteBeforePostMetaIds,
    keptMetaIds,
  };
}

/**
 * Read minor-unit amount from guided-edit / API wire (flat number or `{ amount }` with **numeric** `amount` only).
 * Do not send `amount` as a string; persisted {@link Price} uses numbers after {@link normalizePrice}.
 */
export function readBudgetMinorAmount(
  value:
    | number
    | { amount?: number | null; currency?: string }
    | null
    | undefined
): number {
  if (value == null) return 0;
  if (typeof value === "number") {
    const n = coerceFiniteMinorAmount(value);
    return n ?? 0;
  }
  if (typeof value === "object" && value !== null && "amount" in value) {
    return coerceFiniteMinorAmount((value as { amount?: unknown }).amount) ?? 0;
  }
  return 0;
}

export function validateScheduledCampaignBudget(
  budget: Extract<MarketingCampaignBudget, { type: "scheduled" }>
): CampaignBudgetWindowsValidationError | null {
  return validateBudgetSegments({
    segments: budget.segments,
    requireAtLeastOne: true,
    minSegmentCount: SCHEDULED_BUDGET_MIN_SEGMENTS,
    requireContiguous: true,
  });
}

/**
 * Full ad delivery window from ad sets: earliest `schedule.start_time` and latest
 * `schedule.stop_time` (UTC ISO) across ad sets that define both bounds. Phased campaign
 * budgets must fit inside this window ({@link validateScheduledBudgetWithinAdFlight}).
 */
export function deriveCampaignFlightIsoBoundsFromAdsets(
  adsets: Array<{
    schedule?: { start_time?: string | null; stop_time?: string | null } | null;
  }>
): { flightStartIso: string; flightEndIso: string } | null {
  let minStart: number | null = null;
  let maxEnd: number | null = null;
  for (const a of adsets) {
    const s = a.schedule?.start_time?.trim();
    const e = a.schedule?.stop_time?.trim();
    if (!s || !e) continue;
    const ts = Date.parse(s);
    const te = Date.parse(e);
    if (!Number.isFinite(ts) || !Number.isFinite(te) || te <= ts) continue;
    minStart = minStart === null ? ts : Math.min(minStart, ts);
    maxEnd = maxEnd === null ? te : Math.max(maxEnd, te);
  }
  if (minStart === null || maxEnd === null) return null;
  return {
    flightStartIso: new Date(minStart).toISOString(),
    flightEndIso: new Date(maxEnd).toISOString(),
  };
}

/**
 * Ensures each phased budget segment lies fully inside the ad delivery window
 * `[flightStartIso, flightEndIso]` (campaign / ad set run start → end), and that phased
 * budgets **cover the whole run**: the first phase starts exactly at delivery start and the
 * last phase ends exactly at delivery end (no budget-free gaps at the edges). Call after
 * {@link validateBudgetSegments} / {@link validateScheduledCampaignBudget}.
 *
 * `flightStartIso` / `flightEndIso` are aligned to Meta’s 15m UTC grid (same as
 * {@link snapUtcFlightIsoBoundsToMetaBudgetScheduleGrid} on launch) before comparison, so ad-set
 * run times that are not on the grid still match normalized segment instants.
 */
export function validateScheduledBudgetWithinAdFlight(params: {
  segments: MarketingCampaignBudgetSegment[];
  flightStartIso: string | null | undefined;
  flightEndIso: string | null | undefined;
}): CampaignBudgetWindowsValidationError | null {
  const { segments, flightStartIso, flightEndIso } = params;
  if (segments.length === 0) return null;
  const fsRaw = flightStartIso?.trim();
  const feRaw = flightEndIso?.trim();
  if (!fsRaw || !feRaw) {
    return {
      code: "BOUNDARY_INVALID",
      message:
        "A start and end run schedule are required so scheduled budgets can be checked against the ad delivery window.",
    };
  }
  const { flightStartIso: fs, flightEndIso: fe } =
    snapUtcFlightIsoBoundsToMetaBudgetScheduleGrid({
      flightStartIso: fsRaw,
      flightEndIso: feRaw,
    });
  const flightStart = parseIsoToUnixSeconds(fs);
  const flightEnd = parseIsoToUnixSeconds(fe);
  if (flightStart == null || flightEnd == null) {
    return {
      code: "BOUNDARY_INVALID",
      message: "Run schedule must use valid ISO 8601 start and end times.",
    };
  }
  if (flightEnd <= flightStart) {
    return {
      code: "BOUNDARY_INVALID",
      message: "Run schedule must end after it starts.",
    };
  }
  const inFlightErr = budgetIsoRowsWithinFlightInclusive({
    rows: segments,
    flightStart,
    flightEnd,
    invalidIsoMessage:
      "Each budget phase must use valid ISO 8601 time_start and time_end.",
    beforeFlightMessage:
      "Scheduled budget phases must not start before the ad run schedule.",
    afterFlightMessage:
      "Scheduled budget phases must not end after the ad run schedule.",
  });
  if (inFlightErr) return inFlightErr;

  const first = segments[0];
  const last = segments[segments.length - 1];
  const firstStart = parseIsoToUnixSeconds(first.time_start);
  const lastEnd = parseIsoToUnixSeconds(last.time_end);
  if (firstStart != null && firstStart !== flightStart) {
    return {
      code: "FLIGHT_START_MISMATCH",
      message:
        "The first budget phase must start exactly when the campaign run starts.",
    };
  }
  if (lastEnd != null && lastEnd !== flightEnd) {
    return {
      code: "FLIGHT_END_MISMATCH",
      message:
        "The last budget phase must end exactly when the campaign run ends.",
    };
  }

  return null;
}

/** When no ad set contributes both schedule bounds for a combined delivery window. */
export const SCHEDULED_CAMPAIGN_ADSET_FLIGHT_REQUIRED_MESSAGE =
  "Scheduled budgets require each ad set to define both a start and end run time.";

/**
 * Validates a scheduled campaign budget against segment rules and the combined ad-set
 * delivery window (earliest start / latest end among ad sets with both schedule bounds).
 *
 * Call from the customer **ads-manager** API and `AdsManagerService.publishCampaign`
 * before starting publish Step Functions — keep publish Lambdas free of this duplicate check.
 */
export type ScheduledSegmentMetaEditState = "frozen" | "locked" | "editable";

export const SCHEDULED_SEGMENT_META_LOCKED_MESSAGE =
  "This phase can't be updated on Meta: less than 3 hours remain at the next schedule slot. Extend the phase end or wait until it ends.";

export type ScheduledSegmentMetaEditStateRow = {
  state: ScheduledSegmentMetaEditState;
  /** Set when `state === "locked"`. */
  lockReason?: string;
};

/**
 * Per-segment edit/publish policy for campaigns linked to Meta. Uses the same mapping as
 * {@link budgetScheduleWindowsToEdgePayloads} (advance in-progress start, 3h minimum, omit ended).
 */
export function scheduledSegmentMetaEditStates(params: {
  budget: MarketingCampaignBudgetScheduled;
  timezone?: string;
  linkedToMeta: boolean;
  nowUnixSeconds?: number;
}): ScheduledSegmentMetaEditStateRow[] {
  const { budget, linkedToMeta } = params;
  const nowSec =
    params.nowUnixSeconds ?? Math.floor(Date.now() / 1000);
  const { segments } = budget;

  if (!linkedToMeta) {
    return segments.map((seg) => {
      const end = parseIsoToUnixSeconds(seg.time_end);
      if (end != null && end <= nowSec) {
        return { state: "frozen" as const };
      }
      return { state: "editable" as const };
    });
  }

  const { scheduleRows, scheduleRowSourceSegmentIndices } =
    phasedSegmentsToMetaBaseAndScheduleRows(budget, params.timezone);
  const edgePayloads = budgetScheduleWindowsToEdgePayloads(scheduleRows, {
    nowUnixSeconds: nowSec,
  });
  const postableScheduleRowIndices = new Set(
    edgePayloads.map((p) => p.scheduleRowIndex)
  );
  const segmentIndexToScheduleRowIndex = new Map<number, number>();
  scheduleRowSourceSegmentIndices.forEach((segmentIndex, scheduleRowIndex) => {
    segmentIndexToScheduleRowIndex.set(segmentIndex, scheduleRowIndex);
  });

  return segments.map((seg, segmentIndex) => {
    const end = parseIsoToUnixSeconds(seg.time_end);
    if (end != null && end <= nowSec) {
      return { state: "frozen" as const };
    }

    const scheduleRowIndex = segmentIndexToScheduleRowIndex.get(segmentIndex);
    if (scheduleRowIndex == null) {
      return { state: "editable" as const };
    }

    if (postableScheduleRowIndices.has(scheduleRowIndex)) {
      return { state: "editable" as const };
    }

    return {
      state: "locked" as const,
      lockReason: SCHEDULED_SEGMENT_META_LOCKED_MESSAGE,
    };
  });
}

/** First locked boosted segment message for publish / API gates (linked Meta only). */
export function validateScheduledSegmentMetaEditForLinkedPublish(params: {
  budget: MarketingCampaignBudgetScheduled;
  timezone?: string;
  nowUnixSeconds?: number;
}): string | null {
  const rows = scheduledSegmentMetaEditStates({
    ...params,
    linkedToMeta: true,
  });
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]!;
    if (row.state === "locked") {
      const detail = row.lockReason ?? SCHEDULED_SEGMENT_META_LOCKED_MESSAGE;
      return `Phase ${i + 1}: ${detail}`;
    }
  }
  return null;
}

export function validateScheduledCampaignForAdsManagerPublish(params: {
  budget: MarketingCampaignBudget | undefined;
  adsets: Array<{
    schedule?: { start_time?: string | null; stop_time?: string | null } | null;
  }>;
  /** When set, also block publish if a boosted phase cannot POST after advance + 3h filter. */
  campaignExternalId?: string | null;
  nowUnixSeconds?: number;
}): string | null {
  const { budget, adsets, campaignExternalId, nowUnixSeconds } = params;
  if (budget?.type !== "scheduled") return null;
  const winErr = validateScheduledCampaignBudget(budget);
  if (winErr) return winErr.message;
  const bounds = deriveCampaignFlightIsoBoundsFromAdsets(adsets ?? []);
  if (!bounds) return SCHEDULED_CAMPAIGN_ADSET_FLIGHT_REQUIRED_MESSAGE;
  const flightErr = validateScheduledBudgetWithinAdFlight({
    segments: budget.segments,
    flightStartIso: bounds.flightStartIso,
    flightEndIso: bounds.flightEndIso,
  });
  if (flightErr) return flightErr.message;

  const graphId = campaignExternalId?.trim();
  if (graphId) {
    const metaLockErr = validateScheduledSegmentMetaEditForLinkedPublish({
      budget,
      timezone: timezoneFromCampaignAdsets(
        (adsets ?? []) as MarketingCampaign["adsets"]
      ),
      nowUnixSeconds,
    });
    if (metaLockErr) return metaLockErr;
  }
  return null;
}

/** Meta Graph `budget_schedules` row (subset returned by GET). */
export type MetaBudgetScheduleRowBrief = {
  id: string;
  time_start: number;
  time_end: number;
  /**
   * Echoed from Graph when `GET /{campaign_id}` requests `budget_schedules{...,budget_value,...}`.
   * Use to verify Meta persisted the same increment (minor units, same as `daily_budget`) we POSTed.
   */
  budget_value?: number;
  budget_value_type?: string;
  /**
   * When set by the Meta publish flow, index into the `scheduleRows` list passed to
   * `budgetScheduleWindowsToEdgePayloads` so merge can attach Graph ids after sort/normalize
   * shifts unix bounds.
   */
  scheduleRowIndex?: number;
};

function metaBudgetScheduleSnappedKeyFromUnixBounds(params: {
  time_start: number;
  time_end: number;
}): string {
  const s = snapMetaBudgetScheduleWindowUnixRange(params);
  return `${s.time_start}\0${s.time_end}`;
}

function metaBudgetScheduleSnappedKeyFromIsoSegment(seg: {
  time_start: string;
  time_end: string;
}): string | null {
  const ts = Math.floor(Date.parse(seg.time_start) / 1000);
  const te = Math.floor(Date.parse(seg.time_end) / 1000);
  if (!Number.isFinite(ts) || !Number.isFinite(te)) return null;
  return metaBudgetScheduleSnappedKeyFromUnixBounds({
    time_start: ts,
    time_end: te,
  });
}

/**
 * Writes Meta `budget_schedules` Graph ids onto boosted {@link MarketingCampaignBudgetSegment}s
 * (same windows as derived `scheduleRows`, compared on Meta's 15m-snapped unix timeline).
 *
 * `timezone` must match the derivation used when the rows were POSTed (see
 * {@link phasedSegmentsToMetaBaseAndScheduleRows}); omit for the legacy UTC pacing path.
 */
export function mergeMetaBudgetScheduleRowsIntoScheduledSegments(
  budget: MarketingCampaignBudgetScheduled,
  metaRows: MetaBudgetScheduleRowBrief[],
  timezone?: string
): MarketingCampaignBudgetSegment[] {
  const { scheduleRows } = phasedSegmentsToMetaBaseAndScheduleRows(
    budget,
    timezone
  );

  const idByScheduleRowIndex = new Map<number, string>();
  for (const row of metaRows) {
    if (typeof row.scheduleRowIndex === "number" && row.scheduleRowIndex >= 0) {
      idByScheduleRowIndex.set(row.scheduleRowIndex, row.id);
    }
  }

  const boostedKeys = new Set(
    scheduleRows
      .map((r) =>
        metaBudgetScheduleSnappedKeyFromIsoSegment({
          time_start: r.time_start,
          time_end: r.time_end,
        })
      )
      .filter((k): k is string => k != null)
  );

  const idBySnappedKey = new Map<string, string>();
  for (const row of metaRows) {
    const key = metaBudgetScheduleSnappedKeyFromUnixBounds({
      time_start: row.time_start,
      time_end: row.time_end,
    });
    idBySnappedKey.set(key, row.id);
  }

  return budget.segments.map((seg) => {
    const base: MarketingCampaignBudgetSegment = {
      time_start: seg.time_start,
      time_end: seg.time_end,
      amount: seg.amount,
    };

    const scheduleRowIdx = scheduleRows.findIndex(
      (r) => r.time_start === seg.time_start && r.time_end === seg.time_end
    );
    if (scheduleRowIdx >= 0) {
      const idFromIndex = idByScheduleRowIndex.get(scheduleRowIdx);
      if (idFromIndex) {
        return { ...base, external_id: idFromIndex };
      }
    }

    const key = metaBudgetScheduleSnappedKeyFromIsoSegment(seg);
    if (!key || !boostedKeys.has(key)) {
      return base;
    }
    const id = idBySnappedKey.get(key);
    return id ? { ...base, external_id: id } : base;
  });
}

/**
 * Parses `meta_budget_schedules` from a Meta campaign POST response (e.g. after posting
 * high-demand windows). Invalid shapes yield `[]` so callers can always pass the result to
 * {@link attachMetaBudgetScheduleExternalsToCampaign} (it no-ops on empty arrays).
 */
export function metaBudgetScheduleRowsFromCampaignGraphUpdate(
  result: unknown
): MetaBudgetScheduleRowBrief[] {
  if (result == null || typeof result !== "object") return [];
  const raw = (result as { meta_budget_schedules?: unknown })
    .meta_budget_schedules;
  return Array.isArray(raw) ? (raw as MetaBudgetScheduleRowBrief[]) : [];
}

export function attachMetaBudgetScheduleExternalsToCampaign(
  campaign: MarketingCampaign,
  metaRows: MetaBudgetScheduleRowBrief[]
): MarketingCampaign {
  if (campaign.budget?.type !== "scheduled" || metaRows.length === 0) {
    return campaign;
  }

  return {
    ...campaign,
    budget: {
      type: "scheduled",
      segments: mergeMetaBudgetScheduleRowsIntoScheduledSegments(
        campaign.budget,
        metaRows,
        timezoneFromCampaignAdsets(campaign.adsets)
      ),
    },
  };
}
